// src/ui/PlacementMode.tsx

import React, { useEffect } from 'react';
import { useProjectStore } from '../state/projectStore';
import { BoardView } from './BoardView';
import { GroupList } from './GroupList';
import { CurrentPartPanel } from './CurrentPartPanel';
import './PlacementMode.css';

interface PlacementModeProps {
  onExit: () => void;
}

export const PlacementMode: React.FC<PlacementModeProps> = ({ onExit }) => {
  const {
    name,
    board,
    placements,
    groups,
    progress,
    ui,
    markPlaced,
    skipPlacement,
    selectPlacement,
    nextPlacement,
    prevPlacement,
    setView,
    setZoom,
    setPan
  } = useProjectStore();
  
  // Get current placement
  const currentPlacement = progress.currentPlacementId 
    ? placements.find(p => p.id === progress.currentPlacementId) || null
    : null;
  
  // Get current group
  const currentGroup = progress.currentGroupId
    ? groups.find(g => g.id === progress.currentGroupId) || null
    : null;
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault();
          if (currentPlacement) {
            markPlaced(currentPlacement.id);
            nextPlacement();
          }
          break;
          
        case 's':
        case 'S':
          if (currentPlacement) {
            skipPlacement(currentPlacement.id);
            nextPlacement();
          }
          break;
          
        case 'b':
        case 'B':
        case 'ArrowLeft':
          prevPlacement();
          break;
          
        case 'ArrowRight':
          nextPlacement();
          break;
          
        case 't':
        case 'T':
          setView('top');
          break;
          
        case 'm':
        case 'M':
          setView('bottom');
          break;
          
        case '+':
        case '=':
          setZoom(ui.zoom * 1.2);
          break;
          
        case '-':
        case '_':
          setZoom(ui.zoom / 1.2);
          break;
          
        case 'Escape':
          selectPlacement(null);
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    currentPlacement,
    ui.zoom,
    markPlaced,
    skipPlacement,
    nextPlacement,
    prevPlacement,
    setView,
    setZoom,
    selectPlacement
  ]);
  
  // Calculate stats
  const stats = {
    total: placements.length,
    placed: placements.filter(p => p.status === 'placed').length,
    skipped: placements.filter(p => p.status === 'skipped').length,
    pending: placements.filter(p => p.status === 'pending').length
  };
  
  const percentComplete = stats.total > 0 
    ? Math.round((stats.placed / stats.total) * 100) 
    : 0;
  
  return (
    <div className="placement-mode">
      {/* Header */}
      <header className="placement-header">
        <div className="header-left">
          <h1>{name}</h1>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${percentComplete}%` }}
            />
          </div>
          <span className="progress-text">
            {stats.placed}/{stats.total} ({percentComplete}%)
          </span>
        </div>
        
        <div className="header-center">
          <button 
            className={ui.currentView === 'top' ? 'active' : ''}
            onClick={() => setView('top')}
          >
            Top
          </button>
          <button 
            className={ui.currentView === 'bottom' ? 'active' : ''}
            onClick={() => setView('bottom')}
          >
            Bottom
          </button>
        </div>
        
        <div className="header-right">
          <button onClick={onExit}>Exit</button>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="placement-content">
        {/* Group List Sidebar */}
        <aside className="group-sidebar">
          <GroupList 
            groups={groups}
            currentGroupId={progress.currentGroupId}
            onSelectGroup={(groupId) => {
              // TODO: Implement group selection
            }}
          />
        </aside>
        
        {/* Board View */}
        <main className="board-container">
          <BoardView
            board={board}
            placements={placements}
            currentView={ui.currentView}
            zoom={ui.zoom}
            pan={ui.pan}
            selectedId={ui.selectedPlacementId}
            currentPlacementId={progress.currentPlacementId}
            showLayers={ui.showLayers}
            onSelect={selectPlacement}
            onZoom={setZoom}
            onPan={setPan}
          />
        </main>
        
        {/* Current Part Panel */}
        <aside className="part-panel">
          <CurrentPartPanel
            placement={currentPlacement}
            group={currentGroup}
            onMarkPlaced={() => {
              if (currentPlacement) {
                markPlaced(currentPlacement.id);
                nextPlacement();
              }
            }}
            onSkip={() => {
              if (currentPlacement) {
                skipPlacement(currentPlacement.id);
                nextPlacement();
              }
            }}
            onBack={prevPlacement}
          />
        </aside>
      </div>
      
      {/* Keyboard Shortcuts Help */}
      <footer className="shortcuts-help">
        <span>Space/Enter: Mark Placed</span>
        <span>S: Skip</span>
        <span>B/←: Back</span>
        <span>T: Top View</span>
        <span>M: Bottom View</span>
        <span>+/-: Zoom</span>
      </footer>
    </div>
  );
};
